import axios from "axios";
import { getAccessToken } from "./authSession";
import { API_BASE_URL } from "../config";

const getAuthHeaders = () => {
    const token = getAccessToken();
    if (!token) {
        window.location.href = "/";
        return {};
    }

    return {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    };
};

const getMultipartAuthHeaders = () => {
    const token = getAccessToken();
    if (!token) {
        window.location.href = "/";
        return {};
    }

    return {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
};

export const getSuppliersViewList = async () => {
    const response = await axios.get(`${API_BASE_URL}/suppliers/view/`, getAuthHeaders());
    return response.data;
};

export const getSupplierViewDetail = async (code) => {
    const response = await axios.get(`${API_BASE_URL}/suppliers/view/${code}`, getAuthHeaders());
    return response.data;
};

export const createSupplier = async (data) => {
    const response = await axios.post(`${API_BASE_URL}/dropship/enterprises/`, data, getAuthHeaders());
    return response.data;
};

export const updateSupplier = async (code, data) => {
    const response = await axios.put(`${API_BASE_URL}/dropship/enterprises/${code}`, data, getAuthHeaders());
    return response.data;
};

export const getBusinessStores = async () => {
    const response = await axios.get(`${API_BASE_URL}/business-stores?is_active=true`, getAuthHeaders());
    return response.data;
};

export const getBusinessSupplierStoreSettingsOverview = async (supplierCode) => {
    const response = await axios.get(
        `${API_BASE_URL}/business-suppliers/${supplierCode}/store-settings`,
        getAuthHeaders(),
    );
    return response.data;
};

export const getBusinessSupplierMarkupReport = async () => {
    const response = await axios.get(`${API_BASE_URL}/business-suppliers/markup-report`, getAuthHeaders());
    return response.data;
};

export const getSupplierReconciliationOptions = async () => {
    const response = await axios.get(`${API_BASE_URL}/business-suppliers/reconciliation/options`, getAuthHeaders());
    return response.data;
};

export const runSupplierReconciliation = async ({ period, supplierCode, businessEntityId, depositGroupId, file, ordersFile, balanceFile, dropshipFile, usdUahRate }) => {
    const formData = new FormData();
    formData.append("period", period);
    formData.append("supplier_code", supplierCode);
    if (businessEntityId) {
        formData.append("business_entity_id", businessEntityId);
    }
    if (depositGroupId) {
        formData.append("deposit_group_id", depositGroupId);
    }
    if (usdUahRate) {
        formData.append("usd_uah_rate", usdUahRate);
    }
    if (ordersFile || balanceFile) {
        if (ordersFile) {
            formData.append("orders_file", ordersFile);
        }
        if (balanceFile) {
            formData.append("balance_file", balanceFile);
        }
    } else {
        formData.append("file", file);
        if (dropshipFile) {
            formData.append("dropship_file", dropshipFile);
        }
    }
    const response = await axios.post(
        `${API_BASE_URL}/business-suppliers/reconciliation/run`,
        formData,
        getMultipartAuthHeaders(),
    );
    return response.data;
};

export const downloadSupplierReconciliationReport = async (downloadUrl, filename = "supplier_reconciliation.xlsx") => {
    const response = await axios.get(`${API_BASE_URL}${downloadUrl}`, {
        ...getAuthHeaders(),
        responseType: "blob",
    });
    const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], {
            type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
    );
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
};

export const downloadSupplierOrdersExport = async ({ supplierCode, supplierName, date }) => {
    const response = await axios.get(
        `${API_BASE_URL}/suppliers/view/${encodeURIComponent(supplierCode)}/orders-export.xlsx`,
        {
            ...getAuthHeaders(),
            params: { date },
            responseType: "blob",
        },
    );
    const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], {
            type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
    );
    const link = document.createElement("a");
    link.href = blobUrl;
    const supplierFilename = String(supplierName || supplierCode || "supplier")
        .trim()
        .replace(/[^\p{L}\p{N}._-]+/gu, "_")
        .replace(/^[._]+|[._]+$/g, "") || "supplier";
    link.download = `${supplierFilename}_orders_${date}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    return Number(response.headers["x-export-row-count"] || 0);
};

export const getBusinessStoreSupplierSettings = async (storeId) => {
    const response = await axios.get(
        `${API_BASE_URL}/business-stores/${storeId}/supplier-settings`,
        getAuthHeaders(),
    );
    return response.data;
};

export const upsertBusinessStoreSupplierSettings = async (storeId, supplierCode, data) => {
    const response = await axios.put(
        `${API_BASE_URL}/business-stores/${storeId}/supplier-settings/${supplierCode}`,
        data,
        getAuthHeaders(),
    );
    return response.data;
};
